import { Helmet } from "react-helmet-async";
import { StructuredData } from "./StructuredData";
import { LLMMetaTags } from "./LLMMetaTags";
import { CONTACT, ORG_REF, PERSON_REF, POSTAL_ADDRESS, SITE_URL } from "@/data/entity";

interface SEOWrapperProps {
  title: string;
  description: string;
  keywords?: string[];
  url: string;
  language?: 'es' | 'en';
  ogImage?: string;
  pageType?: 'website' | 'article' | 'service' | 'tool' | 'legal';
  structuredData?: {
    type: 'service' | 'organization' | 'faq' | 'howto' | 'article' | 'collection' | 'profile' | 'contact';
    data: any;
  };
  children: React.ReactNode;
  /**
   * URLs absolutas de las versiones equivalentes ES/EN.
   * Si no se pasan, se derivan del prefijo /en. Pasar `null` explícito
   * cuando NO exista versión equivalente (no se inventan hreflang).
   */
  esUrl?: string | null;
  enUrl?: string | null;
  /**
   * Solo `true` en páginas cuya intención de búsqueda es realmente local.
   * P5.8: por defecto NO se inyecta contexto geográfico ni schema local.
   */
  localBusiness?: boolean;
  /** Ciudad/estado de la página local. Solo se usa si localBusiness=true. */
  localArea?: { city: string; region: string; geoRegion: string };
  summary?: string;
  keyPoints?: string[];
}


export const SEOWrapper = ({
  title,
  description,
  keywords = [],
  url,
  language = 'es',
  ogImage = "https://luisroc.com/media/luis-roc/portrait.png",
  pageType = 'website',
  structuredData,
  children,
  esUrl,
  enUrl,
  localBusiness = false,
  localArea,
}: SEOWrapperProps) => {

  const area = localArea ?? { city: "Houston", region: "Texas", geoRegion: "US-TX" };


  return (
    <>
      {/* La entidad Organization/Person/WebSite se declara una sola vez en
          SiteHead (global). Aquí solo datos específicos de la página. */}
      {structuredData && (
        <StructuredData type={structuredData.type} data={structuredData.data} />
      )}

      {/* Metadata estándar por página */}
      <LLMMetaTags
        title={title}
        description={description}
        url={url}
        language={language}
        keywords={keywords}
        geo={localBusiness ? { region: area.geoRegion, placename: area.city } : null}
      />

      <Helmet>
        {/* Canonical URL */}
        <link rel="canonical" href={url} />

        {/* Hreflang recíprocos: solo se emiten si existe versión equivalente */}

        {/* Author and copyright */}
        <meta name="author" content="Luis ROC" />
        <meta name="copyright" content="ROC Worldwide Agency LLC" />

        {/* Mobile */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

        {/* Enhanced Open Graph */}
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:alt" content={title} />


        {/* Enhanced Twitter Card */}
        <meta name="twitter:image" content={ogImage} />

        {/* Sede física única (Shenandoah, TX). @id estable: es siempre la
            misma entidad, nunca una LocalBusiness distinta por ciudad. */}
        {localBusiness && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              "@id": `${SITE_URL}/#localbusiness`,
              "name": "ROC Worldwide Agency LLC",
              "parentOrganization": ORG_REF,
              "founder": PERSON_REF,
              "description": description,
              "url": `${SITE_URL}/`,
              "telephone": CONTACT.telephone,
              "email": CONTACT.email,
              "address": POSTAL_ADDRESS,
              "areaServed": [
                { "@type": "City", "name": area.city },
                { "@type": "State", "name": area.region }
              ]
            })}
          </script>
        )}

      </Helmet>

      <div className="seo-optimized-page">{children}</div>
    </>
  );
};
